<script lang="ts">
import { computed, defineComponent, Fragment, h, mergeProps, nextTick, ref, shallowRef, unref, watch } from 'vue';

import { deepEqual, ensureValidVNode, keyNames, noop } from '@components/utils';

import {
  makeDisabledProps,
  makeFieldProps,
  makeFilterProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeItemsProps,
  makeLazyProps,
  makeLoadingProps,
  makeLocaleProps,
  makeMenuProps,
  makeReadonlyProps,
  makeSelectProps,
  makeSizeProps,
  makeThemeProps,
  makeTransitionProps,
  makeValidationProps,
  transformItem,
  useField,
  useFilter,
  useFilteredSelectProps,
  useFocus,
  useForm,
  useItems,
  useLocale,
  useMenu,
  useSelect,
  useRender,
  useScrolling
} from '../../composable';
import { CdsCheckbox } from '../CdsCheckbox';
import { CdsIcon } from '../CdsIcon';
import { CdsList, CdsListItem } from '../CdsList';
import { CdsMenu } from '../CdsMenu';
import { CdsTag } from '../CdsTag';
import { CdsTextInput } from '../CdsTextInput';
import { CdsDivider } from '../CdsDivider';

import { CdsHighlight } from './composable/highlight';

import type { IInternalItem } from '../../composable';
import type { PropType } from 'vue';

export const componentName = 'CdsCombobox';
export default defineComponent({
  name: componentName,
  components: { CdsTextInput, CdsMenu, CdsList, CdsListItem, CdsCheckbox, CdsTag, CdsIcon, CdsDivider, CdsHighlight },
  props: {
    /**
     * Разделители для ввода нескольких значений, при вводе будет создавать новый элемент.
     */
    delimiters: {
      type: Array as PropType<string[]>,
      default: () => []
    },

    /**
     * При поиске будет сразу выбирать первый найденный вариант
     */
    autoSelectFirst: {
      type: [Boolean, String] as PropType<boolean | 'exact'>,
      default: undefined
    },

    ...makeSelectProps({
      hideNoData: true
    }),
    ...makeFilterProps({
      filterKeys: ['title']
    }),
    ...makeFieldProps({
      role: 'combobox'
    }),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeLazyProps(),
    ...makeMenuProps(),
    ...makeLocaleProps(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLoadingProps(),
    ...makeSizeProps(),
    ...makeItemsProps({
      returnObject: true
    }),
    ...makeTransitionProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { any }
     */
    'update:modelValue': (value: any) => true,
    /**
     * Событие открытия меню
     * @type { boolean }
     */
    'update:menu': (value: boolean) => true,
    /**
     * Событие при вводе значения в поле
     * @type { string }
     */
    'update:search': (value: string) => true,
    /**
     * Событие выбора всех элементов
     * @type { boolean }
     */
    'update:selectedAll': (value: boolean) => true
  },
  setup(props, { emit, slots }) {
    const { dropdown: comboboxLocale } = useLocale(props);
    const { hasPrependInnerIcon } = useField(props, slots, componentName);
    const { isFocused, focus } = useFocus(props, componentName);
    const { items, transformIn, transformOut } = useItems(props);
    const { internalMenu, menuRef } = useMenu<typeof CdsMenu>(props);
    const { model, selections, selected, selectStrategy, selectedAll, selectedIndeterminate } = useSelect(
      props,
      items,
      transformIn,
      transformOut
    );
    const form = useForm();

    const selectionIndex = ref(-1);
    const isPure = shallowRef(true);
    const listHasFocus = shallowRef(false);
    let cleared = false;

    // Search @todo perhaps need move to composable
    const internalSearch = ref(!props.multiple ? unref(model)[0]?.title ?? '' : '');
    const search = computed<string>({
      get: () => unref(internalSearch),
      set: (value) => {
        internalSearch.value = value;

        if (!props.multiple) {
          model.value = [transformItem(props, value)];
        }

        if (value && props.multiple && props.delimiters?.length) {
          const values = value.split(new RegExp(`(?:${props.delimiters.join('|')})+`));

          if (values.length > 1) {
            values.forEach((val) => {
              val = val.trim();
              if (val) select(transformItem(props, val));
            });
            internalSearch.value = '';
          }
        }

        if (!value) selectionIndex.value = -1;

        isPure.value = !value;
      }
    });
    watch(internalSearch, (value) => {
      if (cleared) {
        nextTick(() => (cleared = false));
      } else if (unref(isFocused) && !unref(internalMenu)) {
        internalMenu.value = true;
      }

      emit('update:search', value);
    });
    watch(model, (value) => {
      if (!props.multiple) {
        internalSearch.value = value[0]?.title ?? '';
      }
    });

    const { filteredItems, getMatches } = useFilter(props, items, () => (unref(isPure) ? '' : unref(search)));

    const displayItems = computed(() => {
      if (props.hideSelected) {
        return unref(filteredItems).filter(
          (filteredItem) => !unref(selections).some((selection) => selection.value === filteredItem.value)
        );
      }
      return unref(filteredItems);
    });

    const highlightFirst = computed(() => {
      const selectFirst =
        props.autoSelectFirst === true ||
        (props.autoSelectFirst === 'exact' && unref(search) === unref(displayItems)[0]?.title);
      return selectFirst && displayItems.value.length > 0 && !unref(isPure) && !listHasFocus.value;
    });

    const hasNoDataItem = computed(() => !props.hideNoData && !unref(displayItems).length);
    const selection = computed(() => unref(selections)[unref(selectionIndex)]);
    const externalValidationValue = computed(() => model.externalValue);

    const comboboxClasses = computed(() => ({
      'cds-combobox--active-menu': unref(internalMenu),
      'cds-combobox--selected': unref(model).length,
      'cds-combobox--selecting-index': unref(selectionIndex) > -1,
      'cds-combobox--ellipsis': props.ellipsis,
      'cds-combobox--disabled': props.disabled,
      [`cds-combobox--${props.multiple ? 'multiple' : 'single'}`]: true
    }));

    const { inputProps } = useFilteredSelectProps(props, CdsTextInput.props, [
      'modelValue',
      'validationValue',
      'focused',
      'appendInnerIcon',
      'clearable',
      'counter'
    ]);

    const inputRef = ref<typeof CdsTextInput>();
    const listRef = ref<typeof CdsList>();

    const { onListScroll, onListKeydown } = useScrolling(listRef, inputRef);

    const showIcon = computed(() => (!props.hideNoData || props.items.length) && props.menuIcon);

    const hasSelectionSlot = computed(() => !!slots.selection);

    const menuDisabled = computed(
      () => (props.hideNoData && !unref(displayItems).length) || props.readonly || unref(form?.isReadonly)
    );

    watch(filteredItems, (value) => {
      if (!value.length && props.hideNoData) internalMenu.value = false;
    });

    watch(isFocused, (val, oldVal) => {
      if (val || val === oldVal) return;

      selectionIndex.value = -1;
      internalMenu.value = false;

      if (
        unref(highlightFirst) &&
        !unref(listHasFocus) &&
        !unref(model).some(({ value }) => value === unref(displayItems)[0].value)
      ) {
        select(unref(displayItems)[0]);
        return;
      }

      if (unref(search)) {
        if (props.multiple) {
          select(transformItem(props, unref(search)));
          return;
        }

        if (!unref(hasSelectionSlot)) return;

        if (unref(model).some(({ title }) => title === unref(search))) {
          internalSearch.value = '';
        } else {
          select(transformItem(props, unref(search)));
        }
      }
    });

    watch(
      () => props.items,
      (value) => {
        if (!unref(isFocused) || !value.length || unref(internalMenu)) return;

        internalMenu.value = true;
      }
    );

    function select(item: IInternalItem) {
      if (props.readonly || props.disabled) return;
      if (item.props.readonly || item.props.disabled) return;

      if (props.multiple) {
        const index = unref(selected).findIndex((selection) => deepEqual(selection, item.value));

        if (index === -1) {
          model.value = [...unref(model), item];
        } else {
          const value = [...unref(model)];
          value.splice(index, 1);
          model.value = value;
        }

        search.value = '';
      } else {
        model.value = [item];
        internalSearch.value = item.title;

        nextTick(() => {
          internalMenu.value = false;
          isPure.value = true;
        });
      }
    }

    function selectAll() {
      if (props.readonly || props.disabled) return;

      if (props.multiple) {
        if (!unref(selectedAll) || unref(selectedIndeterminate)) {
          model.value = [...unref(items)];
        } else {
          model.value = [];
        }

        emit('update:selectedAll', unref(selectedAll));
      }
    }

    function onClear() {
      cleared = true;
      if (props.openOnClear) internalMenu.value = true;
    }

    function onFocusin(event: FocusEvent) {
      focus();

      setTimeout(() => {
        listHasFocus.value = true;
      });
    }

    function onFocusout(event: FocusEvent) {
      listHasFocus.value = false;
    }

    function onAfterLeave() {
      if (unref(isFocused)) {
        isPure.value = true;
        unref(inputRef)?.focus();
      }
    }

    function onKeydown(event: KeyboardEvent) {
      if (props.readonly || unref(form?.isReadonly)) return;

      const selectionStart = unref(inputRef)?.selectionStart;
      const length = unref(selected).length;

      if (
        unref(selectionIndex) > -1 ||
        [keyNames.up, keyNames.down, keyNames.enter, keyNames.home, keyNames.end].includes(event.key)
      ) {
        event.preventDefault();
      }

      if ([keyNames.enter, keyNames.down].includes(event.key)) {
        internalMenu.value = true;
      }

      if ([keyNames.esc].includes(event.key)) {
        internalMenu.value = false;
      }

      if ([keyNames.enter, keyNames.esc, keyNames.tab].includes(event.key)) {
        isPure.value = true;
      }

      if (event.key === keyNames.down) {
        unref(listRef)?.focus('next');
      } else if (event.key === keyNames.up) {
        unref(listRef)?.focus('prev');
      } else if (event.key === keyNames.home) {
        unref(listRef)?.focus('first');
      } else if (event.key === keyNames.end) {
        unref(listRef)?.focus('last');
      }

      if (!props.multiple) return;

      if ([keyNames.backspace, keyNames.delete].includes(event.key)) {
        if (unref(selectionIndex) < 0) {
          if (event.key === keyNames.backspace && !unref(search)) {
            selectionIndex.value = length - 1;
          }

          return;
        }

        const originalSelectionIndex = unref(selectionIndex);

        if (unref(selection)) {
          select(unref(selection));
        }

        selectionIndex.value = originalSelectionIndex >= length - 1 ? length - 2 : originalSelectionIndex;
      }

      if (event.key === keyNames.left) {
        if (unref(selectionIndex) < 0 && selectionStart > 0) return;

        const prev = unref(selectionIndex) > -1 ? unref(selectionIndex) - 1 : length - 1;

        if (unref(selections)[prev]) {
          selectionIndex.value = prev;
        } else {
          selectionIndex.value = -1;
        }
      }

      if (event.key === keyNames.right) {
        if (unref(selectionIndex) < 0) return;

        const next = selectionIndex.value + 1;

        if (unref(selections)[next]) {
          selectionIndex.value = next;
        } else {
          selectionIndex.value = -1;
        }
      }

      if (event.key === keyNames.enter && unref(search)) {
        select(transformItem(props, unref(search)));
        search.value = '';
      }
    }

    function onMousedownControl() {
      if (unref(menuDisabled)) return;

      internalMenu.value = true;
    }

    function onMousedownMenuIcon(event: MouseEvent) {
      if (unref(menuDisabled)) return;

      if (unref(isFocused)) {
        event.preventDefault();
        event.stopPropagation();
      }

      internalMenu.value = !unref(internalMenu);
    }

    function onInputModelValue(value: string) {
      if (value == null || (value === '' && !props.multiple)) model.value = [];
    }

    useRender(() => {
      const hasList = !!(
        !props.hideNoData ||
        unref(displayItems).length ||
        slots['prepend-item'] ||
        slots['append-item'] ||
        slots['no-data']
      );
      const hasCounter = props.counter && props.multiple;
      const isDirty = unref(model).length > 0;

      return h(
        CdsTextInput,
        {
          ref: inputRef,
          ...unref(inputProps),
          modelValue: unref(search),
          'onUpdate:modelValue': (value: string) => {
            search.value = value as any;
            onInputModelValue(value);
          },
          focused: unref(isFocused),
          'onUpdate:focused': (value: boolean) => (isFocused.value = value),
          validationValue: unref(externalValidationValue),
          dirty: isDirty,
          clearable: !props.multiple ? props.clearable : false,
          counter: false,
          size: props.size,
          class: [unref(comboboxClasses), 'cds-combobox'],
          onKeydown,
          'onClick:clear': onClear,
          'onMousedown:control': onMousedownControl
        },
        {
          ...slots,

          default: () =>
            h(Fragment, {}, [
              h(
                CdsMenu,
                {
                  ref: menuRef,
                  modelValue: unref(internalMenu),
                  'onUpdate:modelValue': (value: boolean) => (internalMenu.value = value),
                  activator: 'parent',
                  maxHeight: props.menuHeight,
                  contentClasses: 'cds-combobox__content',
                  disabled: unref(menuDisabled),
                  eager: props.eager,
                  openOnClick: false,
                  closeOnContentClick: false,
                  transition: props.transition,
                  onAfterLeave,
                  ...props.menuProps
                },
                {
                  default: () =>
                    hasList &&
                    h(
                      CdsList,
                      {
                        ref: listRef,
                        selected: unref(selected),
                        selectStrategy: unref(selectStrategy),
                        tabindex: '-1',
                        ariaLive: 'polite',
                        onMousedown: (event: MouseEvent) => event.preventDefault(),
                        onKeydown: onListKeydown,
                        onScrollPassive: onListScroll,
                        onFocusin,
                        onFocusout,
                        ...props.listProps
                      },
                      {
                        default: () =>
                          h(Fragment, {}, [
                            slots['prepend-item']?.() ??
                              (props.multiple &&
                                props.isSelectAll && [
                                  h(
                                    CdsListItem,
                                    {
                                      isClickable: true,
                                      title: comboboxLocale.selectAll,
                                      class: 'cds-combobox__select-all',
                                      onClick: selectAll
                                    },
                                    {
                                      prepend: () =>
                                        h(CdsCheckbox, {
                                          modelValue: unref(selectedAll),
                                          indeterminate: unref(selectedIndeterminate),
                                          tabindex: '-1',
                                          hideMessages: true
                                        })
                                    }
                                  ),
                                  h(CdsDivider)
                                ]),
                            unref(hasNoDataItem) &&
                              (slots['no-data']?.() ?? h(CdsListItem, { title: comboboxLocale.noData })),

                            unref(displayItems).map((item, index) => {
                              const itemProps = mergeProps(item.props, {
                                key: index,
                                disabled: props.readonly || item.props.disabled,
                                onClick: () => select(item)
                              });

                              return (
                                slots.item?.({
                                  item,
                                  index,
                                  props: itemProps
                                }) ??
                                h(
                                  CdsListItem,
                                  { ...itemProps },
                                  {
                                    prepend:
                                      props.multiple && !props.hideSelected
                                        ? ({ isSelected }: { isSelected: boolean }) =>
                                            h(CdsCheckbox, {
                                              modelValue: unref(isSelected),
                                              tabindex: '-1',
                                              hideMessages: true
                                            })
                                        : undefined,
                                    title: () =>
                                      h(CdsHighlight as any, {
                                        isPure: unref(isPure),
                                        item,
                                        matches: getMatches(item)?.title,
                                        length: (unref(search) as any)?.length ?? 0,
                                        name: 'combobox'
                                      })
                                  }
                                )
                              );
                            }),
                            slots['append-item']?.()
                          ])
                      }
                    )
                }
              ),
              unref(selections).map((item, index) => {
                const hasSlot = !!slots.selection;
                const slotContent = hasSlot ? ensureValidVNode(slots.selection!({ item, index })) : undefined;

                if (hasSlot && !slotContent) return undefined;

                return h(
                  'div',
                  {
                    key: item.value,
                    class: [
                      'cds-combobox__selection',
                      { 'cds-combobox__selection--selected': index === unref(selectionIndex) }
                    ]
                  },
                  [
                    slots.selection?.({ item, index }) ??
                      h(Fragment, {}, [
                        h('span', { class: 'cds-combobox__selection--text' }, item.title),
                        props.multiple &&
                          index < unref(selections).length - 1 &&
                          h('span', { class: 'cds-combobox__selection--comma' }, ',')
                      ])
                  ]
                );
              })
            ]),

          ...(unref(hasPrependInnerIcon) || hasCounter
            ? {
                'prepend-inner': () => [
                  props.counter && unref(selected).length > 0
                    ? h(CdsTag, { text: `${unref(selected).length}` })
                    : undefined,
                  slots['prepend-inner']?.()
                ]
              }
            : {}),

          'append-inner': () => [
            slots['append-inner']?.(),
            unref(showIcon)
              ? h(CdsIcon, {
                  name: props.menuIcon,
                  onClick: noop,
                  class: 'cds-combobox__menu-icon',
                  onMousedown: onMousedownMenuIcon
                })
              : undefined
          ]
        }
      );
    });

    return {
      isFocused,
      isPure,
      menu: internalMenu,
      filteredItems,
      select,
      selectAll,
      menuRef,
      listRef,
      textInputRef: inputRef
    };
  }
});
</script>

<style lang="scss">
@import './CdsCombobox';
</style>
